import React, { useMemo, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import {
  filesizeWithinLimits,
  fileExtensionValid,
} from '../../../validation'

function AddPhotoIcon({ size = 30, color = '#fff' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M6.25 26.25C5.5625 26.25 4.97396 26.0052 4.48438 25.5156C3.99479 25.026 3.75 24.4375 3.75 23.75V6.25C3.75 5.5625 3.99479 4.97396 4.48438 4.48438C4.97396 3.99479 5.5625 3.75 6.25 3.75H16.25V6.25H6.25V23.75H23.75V13.75H26.25V23.75C26.25 24.4375 26.0052 25.026 25.5156 25.5156C25.026 26.0052 24.4375 26.25 23.75 26.25H6.25ZM7.5 21.25H22.5L17.8125 15L14.0625 20L11.25 16.25L7.5 21.25ZM21.25 11.25V8.75H18.75V6.25H21.25V3.75H23.75V6.25H26.25V8.75H23.75V11.25H21.25Z" fill={color} />
    </svg>
  )
}

function ImageIcon({ size = 50, color = '#BFBFBF' }) {
  return (
    <svg data-testid="upload-placeholder-icon" width={size} height={size} viewBox="0 0 50 50" style={{ fill: color }} xmlns="http://www.w3.org/2000/svg">
      <path d="M50 44.4444V5.55556C50 2.5 47.5 0 44.4444 0H5.55556C2.5 0 0 2.5 0 5.55556V44.4444C0 47.5 2.5 50 5.55556 50H44.4444C47.5 50 50 47.5 50 44.4444ZM15.2778 29.1667L22.2222 37.5278L31.9444 25L44.4444 41.6667H5.55556L15.2778 29.1667Z" />
    </svg>
  )
}

function ReplaceImageIcon({ size = 30, color = '#fff' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 31 31" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M10.8789 23.9912H18.4249L15.941 20.7398L14.1174 22.9179L12.8911 21.2764L10.8789 23.9912ZM9.62123 27.7793C8.92952 27.7793 8.33737 27.532 7.84479 27.0374C7.3522 26.5429 7.10591 25.9484 7.10591 25.2539V15.1524C7.10591 14.4579 7.3522 13.8634 7.84479 13.3688C8.33737 12.8743 8.92952 12.627 9.62123 12.627H19.6825C20.3742 12.627 20.9664 12.8743 21.459 13.3688C21.9515 13.8634 22.1978 14.4579 22.1978 15.1524V25.2539C22.1978 25.9484 21.9515 26.5429 21.459 27.0374C20.9664 27.532 20.3742 27.7793 19.6825 27.7793H9.62123ZM9.62123 25.2539H19.6825V15.1524H9.62123V25.2539ZM3.96176 10.1016C4.75828 7.80771 6.13647 5.97154 8.09632 4.59311C10.0562 3.21467 12.2414 2.52545 14.6519 2.52545C16.3707 2.52545 18.0004 2.89374 19.541 3.63031C21.0817 4.36688 22.3865 5.4086 23.4555 6.75547V2.52545H25.9708V10.1016H18.4249V7.57621H20.8144C19.9969 6.75547 19.0537 6.12938 17.9847 5.69796C16.9157 5.26654 15.8047 5.05083 14.6519 5.05083C12.954 5.05083 11.3872 5.5033 9.95137 6.40823C8.51554 7.31315 7.43081 8.54428 6.69717 10.1016H3.96176Z" fill={color} />
    </svg>
  )
}

export const UPLOAD_VARIANTS = {
  promoSquare: { width: 150, height: 150, allowDelete: true },
  promoTile: { width: 244, height: 150, allowDelete: true },
  logo: { width: 150, height: 150, allowDelete: false },
  desktopHero: { width: 540, height: 150, allowDelete: true },
  mobileHero: { width: 240, height: 150, allowDelete: true },
  banner: { width: 300, height: 90, allowDelete: true },
}

const internalStyles = {
  deleteLink: {
    fontFamily: 'Roboto Regular, Helvetica, sans-serif',
    fontSize: '14px',
    fontWeight: 400,
    color: '#0E5777',
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    padding: 0,
    textDecoration: 'none',
    lineHeight: 'normal',
  },
  errorText: {
    color: '#d32f2f',
    fontSize: '12px',
    fontFamily: 'Roboto Regular, Helvetica, sans-serif',
    marginTop: '4px',
  },
  editOverlay: {
    width: '50px',
    height: '50px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(243, 243, 243, 0.3)',
    borderRadius: '4px',
    boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.1)',
    cursor: 'pointer',
  },
}

export default function ImageUpload({
  imageUrl,
  imageId,
  onEdit,
  onDelete,
  previewId,
  maxSizeMB = 2,
  accept = '.jpg,.jpeg,.png',
  error,
  validate,
  variant,
  width: widthOverride,
  height: heightOverride,
  sx,
}) {
  const config = variant ? UPLOAD_VARIANTS[variant] : null
  const width = widthOverride ?? config?.width ?? 150
  const height = heightOverride ?? config?.height ?? 150
  const allowDelete = config?.allowDelete ?? true

  const [hovering, setHovering] = useState(false)

  const objectUrl = useMemo(
    () => (imageUrl && typeof imageUrl !== 'string' ? URL.createObjectURL(imageUrl) : null),
    [imageUrl],
  )

  useEffect(() => {
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl) }
  }, [objectUrl])

  const handleFileChange = (file) => {
    const errors = []
    if (maxSizeMB) {
      const sizeError = filesizeWithinLimits(maxSizeMB)(file)
      if (sizeError) errors.push(sizeError)
    }
    const extensions = accept.split(',').map((ext) => ext.replace('.', '').trim())
    const extError = fileExtensionValid(extensions)(file)
    if (extError) errors.push(extError)
    if (errors.length > 0) {
      validate(errors[0])
      return
    }
    validate(null)
    onEdit(file)
  }

  const inputId = `fileInput_${previewId}`

  const containerStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '8px',
    ...sx,
  }

  const imageBoxStyle = {
    width: `${width}px`,
    height: `${height}px`,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#F4F4F4',
    position: 'relative',
    overflow: 'hidden',
  }

  const previewImageStyle = {
    maxWidth: '100%',
    maxHeight: `${height}px`,
    objectFit: variant === 'logo' ? 'contain' : 'cover',
    width: '100%',
    height: '100%',
  }

  // Empty state: gray box with photo icon, clickable to upload
  // On hover, shows dark overlay with add_photo icon (matches Figma "Add Image" variant)
  if (!imageUrl) {
    return (
      <div style={containerStyle}>
        <label
          htmlFor={inputId}
          style={{ ...imageBoxStyle, cursor: 'pointer' }}
          onMouseEnter={() => setHovering(true)}
          onMouseLeave={() => setHovering(false)}
        >
          <ImageIcon size={50} color="#BFBFBF" />
          {hovering && (
            <div style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(42, 45, 54, 0.5)',
              cursor: 'pointer',
              transition: 'background-color 0.2s ease',
            }}>
              <div style={internalStyles.editOverlay}>
                <AddPhotoIcon size={30} color="#fff" />
              </div>
            </div>
          )}
          <input
            id={inputId}
            type="file"
            accept={accept}
            hidden
            onChange={(e) => {
              if (e.target.files[0]) handleFileChange(e.target.files[0])
            }}
          />
        </label>
        {error && <p style={internalStyles.errorText}>{error}</p>}
      </div>
    )
  }

  // Filled/Edit state: image with edit icon overlay + conditional delete link below
  return (
    <div style={containerStyle}>
      <label
        htmlFor={inputId}
        style={imageBoxStyle}
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => setHovering(false)}
      >
        {objectUrl ? (
          <>
            <img src={objectUrl} style={previewImageStyle} alt="" />
            <img id={previewId} src={objectUrl} style={{ visibility: 'hidden', position: 'absolute' }} alt="" />
          </>
        ) : (
          <img src={imageUrl} style={previewImageStyle} alt="" />
        )}
        {/* Edit overlay icon - only visible on hover */}
        {hovering && (
          <div style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(34, 34, 34, 0.5)',
            cursor: 'pointer',
            transition: 'background-color 0.2s ease',
          }}>
            <div style={internalStyles.editOverlay}>
              <ReplaceImageIcon size={30} color="#fff" />
            </div>
          </div>
        )}
        <input
          id={inputId}
          type="file"
          accept={accept}
          hidden
          onChange={(e) => {
            if (e.target.files[0]) handleFileChange(e.target.files[0])
          }}
        />
      </label>
      {allowDelete && (
        <button
          type="button"
          style={internalStyles.deleteLink}
          onClick={onDelete}
        >
          Delete
        </button>
      )}
      {error && <p style={internalStyles.errorText}>{error}</p>}
    </div>
  )
}

ImageUpload.propTypes = {
  imageUrl: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  imageId: PropTypes.number,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  previewId: PropTypes.string.isRequired,
  maxSizeMB: PropTypes.number,
  accept: PropTypes.string,
  error: PropTypes.string,
  validate: PropTypes.func.isRequired,
  variant: PropTypes.oneOf(['promoSquare', 'promoTile', 'logo', 'desktopHero', 'mobileHero', 'banner']),
  width: PropTypes.number,
  height: PropTypes.number,
  sx: PropTypes.object,
}
